import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import {
  REFERRER_TYPE_OPTIONS,
  ReferrerType,
  UserTypeOption,
  userTypeLabel,
} from './userTypes';
import './platform-stats-user-types.scss';

interface StageCounts {
  signedUp: number;
  initiated: number;
  activated: number;
}

interface FunnelDay extends StageCounts {
  day: string;
  byReferrerType: Partial<Record<ReferrerType, StageCounts>>;
}

interface SignupFunnelResponse {
  success: boolean;
  from: string;
  to: string;
  referrerType: ReferrerType | null;
  days: FunnelDay[];
  totals: StageCounts & {
    byReferrerType: Partial<Record<ReferrerType, StageCounts>>;
  };
  referrerTypes: UserTypeOption<ReferrerType>[];
}

type StageKey = keyof StageCounts;

const STAGES: Array<{ key: StageKey; label: string; sub: string }> = [
  { key: 'signedUp', label: 'Sign Up', sub: '1st WhatsApp message' },
  { key: 'initiated', label: 'Initiated', sub: 'account connected' },
  { key: 'activated', label: 'Activated', sub: 'onboarding complete' },
];

const DAY_MS = 24 * 60 * 60 * 1000;
const isoDay = (d: Date) => d.toISOString().slice(0, 10);
const defaultTo = () => isoDay(new Date());
const defaultFrom = () => isoDay(new Date(Date.now() - 13 * DAY_MS));

const fmtDay = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

const pct = (num: number, den: number) =>
  den > 0 ? `${Math.round((num / den) * 100)}%` : '—';

const StageCell: FC<{
  counts: StageCounts;
  stage: StageKey;
  breakdown: Partial<Record<ReferrerType, StageCounts>>;
  order: ReferrerType[];
}> = ({ counts, stage, breakdown, order }) => (
  <td className='stage-cell'>
    <div className='stage-total'>{counts[stage]}</div>
    <div className='stage-breakdown'>
      {order.map((type) => {
        const n = breakdown[type]?.[stage] ?? 0;
        if (n === 0) return null;
        return (
          <span key={type} className={`type-chip type-${type}`}>
            {userTypeLabel(type)} <b>{n}</b>
          </span>
        );
      })}
    </div>
  </td>
);

/**
 * Daily sign-up funnel by referrer type (Onboarding tab).
 *
 * One row per sign-up day (UTC). A sign-up is a WhatsApp onboarding session
 * whose invitee actually messaged us; Initiated / Activated are how many of
 * that day's sign-ups have since connected an account / completed onboarding.
 * Every cell is broken down by the *referrer's* current user type.
 */
const SignupFunnelPanel: FC = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [referrerType, setReferrerType] = useState<'' | ReferrerType>('');
  const [data, setData] = useState<SignupFunnelResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!from || !to) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const params = new URLSearchParams({ from, to });
      if (referrerType) params.set('referrerType', referrerType);
      const res = await api.get<SignupFunnelResponse>(
        `/platform-stats/user-types/signup-funnel?${params}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setData(res.data);
    } catch {
      setError('Failed to load sign-up funnel');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, from, to, referrerType]);

  useEffect(() => {
    load();
  }, [load]);

  const options = data?.referrerTypes ?? REFERRER_TYPE_OPTIONS;

  const typeOrder = useMemo<ReferrerType[]>(
    () =>
      options
        .map((o) => o.value)
        .filter((t) => (data?.totals.byReferrerType[t]?.signedUp ?? 0) > 0),
    [options, data],
  );

  return (
    <section className='activation-section signup-funnel'>
      <h3>Daily sign-ups by referrer type</h3>
      <p className='activation-note'>
        One row per sign-up day. <em>Sign Up</em> is the first inbound WhatsApp
        message, <em>Initiated</em> is account connected, <em>Activated</em> is
        onboarding complete — Initiated and Activated are how many of that
        day&apos;s sign-ups have reached the stage so far. Each cell is split by
        the <em>referrer&apos;s</em> user type (set on the Users tab); sign-ups
        with no referrer show as “No referrer”.
      </p>

      <div className='activation-controls signup-funnel-controls'>
        <label>
          Signed up between
          <input
            type='date'
            value={from}
            max={to || undefined}
            onChange={(e) => setFrom(e.target.value)}
          />
        </label>
        <label>
          and
          <input
            type='date'
            value={to}
            min={from || undefined}
            onChange={(e) => setTo(e.target.value)}
          />
        </label>
        <label>
          Referrer type
          <select
            value={referrerType}
            onChange={(e) =>
              setReferrerType(e.target.value as '' | ReferrerType)
            }
          >
            <option value=''>All referrer types</option>
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        {data && (
          <span className='stat-badge'>
            <strong>{data.totals.signedUp}</strong> sign-ups ·{' '}
            <strong>{data.totals.initiated}</strong> initiated (
            {pct(data.totals.initiated, data.totals.signedUp)}) ·{' '}
            <strong>{data.totals.activated}</strong> activated (
            {pct(data.totals.activated, data.totals.initiated)} of initiated)
          </span>
        )}
      </div>

      {error && <div className='msg error'>{error}</div>}

      {loading && !data ? (
        <div className='loading'>Loading...</div>
      ) : data ? (
        <div className='signup-funnel-table-wrap'>
          <table className='activation-table signup-funnel-table'>
            <thead>
              <tr>
                <th>Day</th>
                {STAGES.map((s) => (
                  <th key={s.key}>
                    {s.label}
                    <small>{s.sub}</small>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.days.length === 0 && (
                <tr>
                  <td colSpan={4} className='empty'>
                    No sign-ups in this range.
                  </td>
                </tr>
              )}
              {data.days.map((d) => (
                <tr key={d.day}>
                  <td className='day-cell'>
                    {fmtDay(d.day)}
                    <small>{d.day}</small>
                  </td>
                  {STAGES.map((s) => (
                    <StageCell
                      key={s.key}
                      counts={d}
                      stage={s.key}
                      breakdown={d.byReferrerType}
                      order={typeOrder}
                    />
                  ))}
                </tr>
              ))}
            </tbody>
            {data.days.length > 0 && (
              <tfoot>
                <tr>
                  <td className='day-cell'>
                    Total
                    <small>
                      {data.from} → {data.to}
                    </small>
                  </td>
                  {STAGES.map((s) => (
                    <StageCell
                      key={s.key}
                      counts={data.totals}
                      stage={s.key}
                      breakdown={data.totals.byReferrerType}
                      order={typeOrder}
                    />
                  ))}
                </tr>
                <tr className='conversion-row'>
                  <td className='day-cell'>Step conversion</td>
                  <td className='num'>—</td>
                  <td className='num'>
                    {pct(data.totals.initiated, data.totals.signedUp)}
                    <small>of sign-ups</small>
                  </td>
                  <td className='num'>
                    {pct(data.totals.activated, data.totals.initiated)}
                    <small>of initiated</small>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      ) : null}
    </section>
  );
};

export default SignupFunnelPanel;
